"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form, Select } from "antd";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";
import { routes } from "~/core/constants/routes";
import { useOrgMembersData } from "~/core/hooks/use-org-members";

import useCurrentUser from "~/lib/organizations/hooks/use-current-user";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { SELECT_INPUT_FOCUS_DELAY } from "../../constants";
import { useGlobalNewOpportunityModalContext } from "../../context";
import { useGlobalCreateOpportunityStore } from "../../store";
import { DDRendererItem } from "../../types";
import DDClearIcon from "../DDClearIcon";

import { DefaultDDRenderer } from "./DefaultDDRenderer";

export default function TeamMemberSelect() {
  const currentUser = useCurrentUser();
  const { form } = useGlobalNewOpportunityModalContext();
  const ref = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    teamMembers,
    setTeamMembers,
    setSelectedTeamMember,
    selectedTeamMember,
    isOpenGlobalCreateOpportunity,
    resetTeamMemberSelection,
  } = useGlobalCreateOpportunityStore();

  const {
    loadingOrgMembers,
    teamMembers: fetchedTeamMembers,
    refetchOrgTeamMembersData,
  } = useOrgMembersData();

  const options = useMemo(() => {
    return fetchedTeamMembers?.map(item => ({
      id: item.user_id,
      label: item.display_name,
      active: selectedTeamMember?.id === item.user_id,
      searchableText: item.display_name,
      metadata: {
        label: item.display_name,
      },
    }));
  }, [fetchedTeamMembers, selectedTeamMember?.id]);

  useEffect(() => {
    const filteredOptions = options?.filter(
      option => option.id !== selectedTeamMember?.id,
    );
    setTeamMembers(
      selectedTeamMember
        ? [{ ...selectedTeamMember, active: true }, ...(filteredOptions || [])]
        : options || [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedTeamMembers]);

  useEffect(() => {
    if (
      !form?.getFieldValue(
        SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER &&
          isOpenGlobalCreateOpportunity,
      )
    ) {
      form?.setFieldsValue({
        [SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER]: currentUser?.user_id,
      });
      if (options) {
        setSelectedTeamMember(
          options.find(
            item => item.id === currentUser?.user_id,
          ) as DDRendererItem,
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedTeamMembers, isOpenGlobalCreateOpportunity]);

  const handleDropdownClose = () => {
    setIsOpen(false);
    // Focus the select after closing
    setTimeout(() => {
      ref.current?.focus();
    }, SELECT_INPUT_FOCUS_DELAY);
  };

  return (
    <Form.Item name={SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER} label="Owner">
      <Select
        allowClear={{ clearIcon: <DDClearIcon /> }}
        onClear={() => {
          form?.resetFields([SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER]);
          resetTeamMemberSelection();
        }}
        placeholder="Select owner"
        ref={ref}
        onDropdownVisibleChange={e => {
          setIsOpen(e);
        }}
        open={isOpen}
        labelRender={e => {
          const teamMember = selectedTeamMember;
          return teamMember?.label;
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        suffixIcon={
          selectedTeamMember ? (
            <DDClearIcon asSuffix />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} />
          )
        }
        value={form?.getFieldValue(SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER)}
        dropdownRender={() => (
          <DefaultDDRenderer
            items={teamMembers}
            onItemSelect={e => {
              form?.setFieldsValue({
                [SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER]: e.id as string,
              });
              setIsOpen(false);
              setSelectedTeamMember(e);
              ref.current?.focus();
            }}
            onCommandKeydown={e => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                handleDropdownClose();
              }
            }}
            emptyContent={
              <div className="flex flex-col items-center justify-center gap-3 text-center text-xs text-zinc-600">
                <p>
                  No team members with this keyword. You can{" "}
                  <span>
                    <Link href={routes.settings.organizationMembers}>
                      invite
                    </Link>
                  </span>{" "}
                  new team members from here
                </p>
                <Button
                  type="primary"
                  size="small"
                  loading={loadingOrgMembers}
                  onClick={() => refetchOrgTeamMembersData()}
                >
                  Retry
                </Button>
              </div>
            }
          />
        )}
      />
    </Form.Item>
  );
}
